import { useEffect, useRef, useState } from 'react';
import { Link, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import {
  applyDeveloper,
  fetchMyDeveloper,
  issueClientSecret,
  updateOAuthClient,
  fetchMyAppListings,
  updateMyAppListing,
  deleteMyAppListing,
  uploadAppIcon,
  MAX_REDIRECT_URIS,
  MAX_APP_LISTINGS,
  APP_LISTING_NAME_MAX,
  type DailyUsage,
  type DeveloperInfo,
  type AppListingInfo,
  type AppListingStatus,
} from '../../api/developers';
import { toneForApp } from '../../lib/connectedApps';
import { useAuth } from '../../auth/AuthContext';
import { OPERATOR } from '../legal/meta';
import Spinner from '../../components/Spinner';
import NewAppListingModal from '../../components/NewAppListingModal';
import { usePagination, Pagination } from '../../components/SimplePagination';
import { fetchMyAnimations, updateAnimationApps } from '../../api/client';
import type { Animation } from '../../types';

// ApiDocs.tsx と同じ流儀（未設定時はドキュメント表示用のダミーホストにフォールバック）。
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.example.com';

type MyDeveloper = DeveloperInfo & { usage: DailyUsage[] };

/** Credential ファイル（VRoid Hub 互換の JSON 形式）をダウンロードさせる。 */
function downloadClientCredential(params: { clientId: string; clientSecret: string; clientSecretPrefix: string }) {
  const credential = {
    format_version: 1,
    service: 'vrma-hub',
    client_id: params.clientId,
    client_secret: params.clientSecret,
    client_secret_prefix: params.clientSecretPrefix,
    token_endpoint: `${API_BASE}/oauth/token`,
    issued_at: new Date().toISOString(),
  };
  const blob = new Blob([JSON.stringify(credential, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vrma-hub-credential-${params.clientId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const STATUS_LABEL: Record<DeveloperInfo['status'], string> = {
  pending: '審査中',
  approved: '承認済み',
  rejected: '却下',
  suspended: '停止中',
};

const STATUS_BADGE_CLASS: Record<DeveloperInfo['status'], string> = {
  pending: 'badge badge--pending',
  approved: 'badge badge--approved',
  rejected: 'badge badge--rejected',
  suspended: 'badge badge--suspended',
};

function StatusBadge({ status }: { status: DeveloperInfo['status'] }) {
  return <span className={STATUS_BADGE_CLASS[status]}>{STATUS_LABEL[status]}</span>;
}

function ApplyForm({
  rejected,
  onSubmitted,
}: {
  rejected: boolean;
  onSubmitted: (dev: DeveloperInfo) => void;
}) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError('利用規約等への同意が必要です。');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const dev = await applyDeveloper({ termsAccepted });
      onSubmitted(dev);
    } catch (e) {
      setError(String(e));
      setSubmitting(false);
    }
  };

  return (
    <>
      {rejected && (
        <div className="notice" style={{ marginBottom: 20 }}>
          今回の申請は却下されました。内容を見直したうえで再申請できます。
        </div>
      )}
      <p>
        開発者登録を行うと、OAuth クライアントの発行や API の利用、連携アプリケーションの掲載申請ができるようになります。
        以下の規約をご確認のうえ、同意して申請してください。
      </p>
      <form className="form" onSubmit={onSubmit}>
        <div className="agree-box">
          <ul className="agree-box__list">
            <li>
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                利用規約 <span className="agree-box__external" aria-hidden="true">↗</span>
              </Link>
            </li>
            <li>
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                プライバシーポリシー <span className="agree-box__external" aria-hidden="true">↗</span>
              </Link>
            </li>
            <li>
              <Link to="/guidelines" target="_blank" rel="noopener noreferrer">
                コミュニティガイドライン <span className="agree-box__external" aria-hidden="true">↗</span>
              </Link>
            </li>
          </ul>
          <label className="agree-box__checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            上記の内容をすべて確認し、同意します
          </label>
        </div>
        {!termsAccepted && (
          <p style={{ opacity: 0.8, fontSize: 13 }}>
            チェックを入れると送信できるようになります。
          </p>
        )}

        {error && <div className="viewer__error">{error}</div>}

        <div>
          <button type="submit" className="btn btn--primary" disabled={submitting || !termsAccepted}>
            {submitting ? '送信中…' : rejected ? '再申請する' : '申請する'}
          </button>
        </div>
      </form>
    </>
  );
}

/**
 * OAuthClientSection 内で Client ID の直下に表示する client_secret 管理サブコンポーネント。
 * 表示体裁は Client ID 行に合わせ、独立見出しは持たない。
 */
function ClientSecretField({
  dev,
  onUpdated,
}: {
  dev: MyDeveloper;
  onUpdated: (dev: DeveloperInfo) => void;
}) {
  const [hasSecret, setHasSecret] = useState(!!dev.hasClientSecret);
  const [prefix, setPrefix] = useState(dev.clientSecretPrefix);
  const [plainSecret, setPlainSecret] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [reissueModalOpen, setReissueModalOpen] = useState(false);

  const issue = async () => {
    setIssuing(true);
    setError('');
    try {
      const { clientSecret, clientSecretPrefix } = await issueClientSecret();
      setPlainSecret(clientSecret);
      setPrefix(clientSecretPrefix);
      setHasSecret(true);
      setCopied(false);
      onUpdated({ ...dev, hasClientSecret: true, clientSecretPrefix });
    } catch (e) {
      setError(String(e));
    } finally {
      setIssuing(false);
    }
  };

  const reissue = async () => {
    await issue();
    setReissueModalOpen(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainSecret);
      setCopied(true);
    } catch {
      setError('コピーに失敗しました。手動で選択してコピーしてください。');
    }
  };

  return (
    <div id="client-secret">
      <p>
        Client Secret:{' '}
        {hasSecret ? (
          <>
            <code className="code-inline">{prefix}</code>{' '}
            <button className="btn" onClick={() => setReissueModalOpen(true)} disabled={issuing} type="button">
              {issuing ? '発行中…' : '再発行'}
            </button>
          </>
        ) : (
          <>
            未発行{' '}
            <button className="btn btn--primary" onClick={issue} disabled={issuing} type="button">
              {issuing ? '発行中…' : 'client_secret を発行'}
            </button>
          </>
        )}
      </p>

      {!hasSecret && (
        <div className="notice" style={{ marginBottom: 16 }}>
          OAuth 連携（/oauth/token・/oauth/revoke）の利用には client_secret の発行が必須です。
        </div>
      )}

      {plainSecret && (
        <div className="notice" style={{ marginBottom: 16 }}>
          このシークレットは今だけ表示されます。再表示はできませんので、安全な場所に保管してください。
          <pre className="code-block" style={{ marginTop: 8 }}>
            <code>{plainSecret}</code>
          </pre>
          <button className="btn" onClick={copy} type="button">
            {copied ? 'コピーしました' : 'コピー'}
          </button>{' '}
          <button
            className="btn"
            type="button"
            onClick={() =>
              dev.clientId &&
              downloadClientCredential({ clientId: dev.clientId, clientSecret: plainSecret, clientSecretPrefix: prefix ?? '' })
            }
            disabled={!dev.clientId}
          >
            Credential ファイルをダウンロード
          </button>
          <p style={{ opacity: 0.8, fontSize: 13, marginTop: 8, marginBottom: 0 }}>
            Credential ファイルは再ダウンロードできません（再発行すると旧シークレットは失効します）。
          </p>
        </div>
      )}

      {error && <div className="viewer__error">{error}</div>}

      <ReissueSecretConfirmModal
        open={reissueModalOpen}
        onCancel={() => setReissueModalOpen(false)}
        onConfirm={reissue}
        confirming={issuing}
      />
    </div>
  );
}

/**
 * client_secret 再発行の確認モーダル。実装は PublishConfirmModal.tsx の作法を踏襲
 * （Escape で閉じる・body スクロールロック・オープン時に主要ボタンへフォーカス・バックドロップクリックで閉じる）。
 */
function ReissueSecretConfirmModal({
  open,
  onCancel,
  onConfirm,
  confirming,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="reissue-secret-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="reissue-secret-title" className="modal__title">
            client_secret を再発行しますか？
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>
        <p className="modal__description">
          再発行すると現在の client_secret は無効化され、旧 secret を使用している連携は動作しなくなります。
        </p>
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={confirming}
            ref={confirmBtnRef}
          >
            {confirming ? '処理中…' : '再発行する'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OAuthClientSection({
  dev,
  onUpdated,
}: {
  dev: MyDeveloper;
  onUpdated: (dev: DeveloperInfo) => void;
}) {
  const [redirectUris, setRedirectUris] = useState<string[]>(dev.redirectUris ?? []);
  const [newUri, setNewUri] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const save = async (next: string[]) => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const updated = await updateOAuthClient(next);
      setRedirectUris(updated.redirectUris ?? []);
      onUpdated(updated);
      setSaved(true);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const addUri = async () => {
    const uri = newUri.trim();
    if (!uri) return;
    if (redirectUris.includes(uri)) {
      setError('既に登録されている redirect_uri です。');
      return;
    }
    await save([...redirectUris, uri]);
    setNewUri('');
  };

  const removeUri = async (uri: string) => {
    await save(redirectUris.filter((u) => u !== uri));
  };

  const copyClientId = async () => {
    if (!dev.clientId) return;
    try {
      await navigator.clipboard.writeText(dev.clientId);
      setCopied(true);
    } catch {
      setError('コピーに失敗しました。手動で選択してコピーしてください。');
    }
  };

  const atLimit = redirectUris.length >= MAX_REDIRECT_URIS;

  return (
    <section id="oauth-client" style={{ marginTop: 24 }}>
      <h3>OAuth クライアント</h3>

      <p>
        Client ID:{' '}
        {dev.clientId ? (
          <>
            <code className="code-inline">{dev.clientId}</code>{' '}
            <button className="btn" onClick={copyClientId} type="button">
              {copied ? 'コピーしました' : 'コピー'}
            </button>
          </>
        ) : (
          '未発行'
        )}
      </p>

      <ClientSecretField dev={dev} onUpdated={onUpdated} />

      <p>
        redirect_uri（{redirectUris.length}/{MAX_REDIRECT_URIS}）
      </p>
      {redirectUris.length === 0 ? (
        <p>登録済みの redirect_uri はありません。</p>
      ) : (
        <ul>
          {redirectUris.map((uri) => (
            <li key={uri}>
              <code className="code-inline">{uri}</code>{' '}
              <button className="btn" onClick={() => removeUri(uri)} disabled={saving} type="button">
                削除
              </button>
            </li>
          ))}
        </ul>
      )}

      {atLimit ? (
        <div className="notice" style={{ marginTop: 12 }}>
          redirect_uri は最大 {MAX_REDIRECT_URIS} 件までです。追加するには先に不要なものを削除してください。
        </div>
      ) : (
        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="newRedirectUri">redirect_uri を追加</label>
          <input
            id="newRedirectUri"
            value={newUri}
            onChange={(e) => setNewUri(e.target.value)}
            placeholder="https://example.com/callback"
          />
          <button
            className="btn btn--primary"
            onClick={addUri}
            disabled={saving || !newUri.trim()}
            type="button"
            style={{ alignSelf: 'flex-start' }}
          >
            {saving ? '保存中…' : '追加'}
          </button>
        </div>
      )}

      {saved && !error && (
        <div className="notice" style={{ marginTop: 12 }}>
          保存しました。
        </div>
      )}
      {error && <div className="viewer__error">{error}</div>}
    </section>
  );
}

const APP_DISTRIBUTION_PAGE_SIZE = 20;
// 1ページ最大 100 件想定（DEFAULT_LIMIT）なので、20 ページで実質 2000 件まで取得する。
const MAX_FETCH_PAGES = 20;

function AppDistributionSection({ dev }: { dev: MyDeveloper }) {
  const [animations, setAnimations] = useState<Animation[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [initial, setInitial] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      // nextCursor が無くなるまで全ページを辿って全件取得する。暴走防止に MAX_FETCH_PAGES で打ち切る。
      const all: Animation[] = [];
      let cursor: string | undefined;
      let pageCount = 0;
      let hasMore = false;
      try {
        do {
          const { items, nextCursor } = await fetchMyAnimations({ cursor });
          all.push(...items);
          cursor = nextCursor;
          pageCount++;
          hasMore = !!cursor;
        } while (cursor && pageCount < MAX_FETCH_PAGES);
        if (cancelled) return;
        setTruncated(hasMore && pageCount >= MAX_FETCH_PAGES);
        setAnimations(all);
        const map: Record<string, boolean> = {};
        for (const a of all) {
          map[a.id] = !!dev.clientId && (a.appClientIds ?? []).includes(dev.clientId);
        }
        setSelected(map);
        setInitial(map);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dev.clientId]);

  const toggle = (id: string) => {
    setMessage('');
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const dirtyIds = (animations ?? []).filter((a) => selected[a.id] !== initial[a.id]).map((a) => a.id);

  const needle = filter.trim().toLowerCase();
  const filtered = (animations ?? []).filter((a) => !needle || a.title.toLowerCase().includes(needle));

  const { page, setPage, totalPages, pageItems } = usePagination(filtered, APP_DISTRIBUTION_PAGE_SIZE);

  // 絞り込み条件が変わったら、ページ範囲外になる前に明示的に1ページ目へ戻す。
  useEffect(() => {
    setPage(1);
  }, [filter, setPage]);

  const apply = async () => {
    if (!animations || dirtyIds.length === 0 || !dev.clientId) return;
    setSaving(true);
    setMessage('');
    setError('');

    const results = await Promise.allSettled(
      dirtyIds.map(async (id) => {
        const anim = animations.find((a) => a.id === id);
        const current = new Set(anim?.appClientIds ?? []);
        if (selected[id]) {
          current.add(dev.clientId as string);
        } else {
          current.delete(dev.clientId as string);
        }
        const updated = await updateAnimationApps(id, [...current]);
        return { id, updated };
      }),
    );

    const nextAnimations = [...animations];
    const nextInitial = { ...initial };
    let failed = 0;
    for (const r of results) {
      if (r.status === 'fulfilled') {
        const idx = nextAnimations.findIndex((a) => a.id === r.value.id);
        if (idx !== -1) nextAnimations[idx] = r.value.updated;
        nextInitial[r.value.id] = selected[r.value.id];
      } else {
        failed++;
      }
    }
    setAnimations(nextAnimations);
    setInitial(nextInitial);
    setSaving(false);
    setMessage(failed === 0 ? '保存しました。' : `${failed}件の保存に失敗しました。`);
  };

  return (
    <section id="app-distribution" style={{ marginTop: 24 }}>
      <h3>アニメーション配信設定</h3>
      <p>
        チェックしたアプリからは、公開範囲が非公開でも全ユーザーが閲覧できます。サイト上の表示は公開範囲の設定に従います。
      </p>

      {loading && <Spinner />}
      {error && <p className="viewer__error">{error}</p>}

      {!loading && !error && animations && (
        animations.length === 0 ? (
          <p>まだ投稿がありません。</p>
        ) : (
          <>
            {truncated && (
              <div className="notice" style={{ marginBottom: 16 }}>
                投稿が多いため先頭 {animations.length} 件のみを表示しています。
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <input
                type="text"
                placeholder="タイトルで絞り込み"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{ width: 280 }}
              />
            </div>

            {filtered.length === 0 ? (
              <p>該当するアニメーションはありません。</p>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>アニメーション</th>
                      <th>公開範囲</th>
                      <th>{dev.appName || '(アプリ未設定)'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {a.thumbnailUrl && (
                              <img
                                src={a.thumbnailUrl}
                                alt=""
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                              />
                            )}
                            <span>{a.title}</span>
                          </div>
                        </td>
                        <td>{(a.visibility ?? 'public') === 'public' ? '公開' : '非公開'}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[a.id]}
                            disabled={!dev.clientId || saving}
                            onChange={() => toggle(a.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )}

            {dirtyIds.length > 0 && (
              <div className="notice" style={{ marginTop: 12 }}>
                未保存の変更: {dirtyIds.length}件（他のページ・絞り込み範囲外にもある場合があります）
              </div>
            )}

            {message && (
              <div className="notice" style={{ marginTop: 12 }}>
                {message}
              </div>
            )}

            <button
              type="button"
              className="btn btn--primary"
              style={{ marginTop: 12 }}
              onClick={apply}
              disabled={saving || dirtyIds.length === 0}
            >
              {saving ? '保存中…' : '適用'}
            </button>
          </>
        )
      )}
    </section>
  );
}

const APP_LISTING_STATUS_LABEL: Record<AppListingStatus, string> = {
  pending: '審査中',
  approved: '掲載中',
  rejected: '却下',
  suspended: '停止中',
};

function AppListingStatusBadge({ status }: { status: AppListingStatus }) {
  return <span className={STATUS_BADGE_CLASS[status]}>{APP_LISTING_STATUS_LABEL[status]}</span>;
}

/** 一覧の行内に展開する編集フォーム（保存で PUT → 再審査、キャンセルで閉じる）。 */
function AppListingEditForm({
  listing,
  onSaved,
  onCancel,
}: {
  listing: AppListingInfo;
  onSaved: (l: AppListingInfo) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(listing.name);
  const [url, setUrl] = useState(listing.url);
  const [iconKey, setIconKey] = useState<string | undefined>(listing.iconKey);
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string | undefined>(listing.iconUrl);
  const objectUrlRef = useRef('');

  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // アンマウント時にプレビュー用の objectURL を解放する（Upload.tsx のサムネイル選択と同じ作法）。
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIconError('');
    setUploadingIcon(true);
    try {
      const key = await uploadAppIcon(file);
      setIconKey(key);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const preview = URL.createObjectURL(file);
      objectUrlRef.current = preview;
      setIconPreviewUrl(preview);
    } catch (err) {
      setIconError(String(err));
    } finally {
      setUploadingIcon(false);
    }
  };

  const submit = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) {
      setFormError('表示名とURLを入力してください。');
      return;
    }
    if (!/^https:\/\//.test(trimmedUrl)) {
      setFormError('URLは https:// で始まる必要があります。');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const updated = await updateMyAppListing(listing.appId, { name: trimmedName, url: trimmedUrl, iconKey });
      onSaved(updated);
    } catch (e) {
      setFormError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-listings__edit">
      <div className="field">
        <label>アプリアイコン</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span className={`app-icon app-icon--md ${!iconPreviewUrl ? `app-icon--tone${toneForApp(listing)}` : ''}`}>
            {iconPreviewUrl ? <img src={iconPreviewUrl} alt="" /> : (name || '?').charAt(0).toUpperCase()}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label className="btn" style={{ alignSelf: 'flex-start' }}>
              画像を変更
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                disabled={uploadingIcon}
                onChange={handleIconChange}
              />
            </label>
            {uploadingIcon && <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>アップロード中…</span>}
            {iconError && <span className="viewer__error">{iconError}</span>}
          </div>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`appListingName-${listing.appId}`}>
          表示名 ({name.length}/{APP_LISTING_NAME_MAX})
        </label>
        <input
          id={`appListingName-${listing.appId}`}
          value={name}
          maxLength={APP_LISTING_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`appListingUrl-${listing.appId}`}>アプリの URL</label>
        <input
          id={`appListingUrl-${listing.appId}`}
          value={url}
          placeholder="https://example.com"
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>

      <p className="app-listings__hint">保存すると再審査になります</p>

      {formError && <div className="viewer__error">{formError}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--primary" disabled={submitting || uploadingIcon} onClick={submit}>
          {submitting ? '保存中…' : '保存して再申請する'}
        </button>
        <button type="button" className="btn" disabled={submitting} onClick={onCancel}>
          キャンセル
        </button>
      </div>
    </div>
  );
}

/** 一覧の1行（アイコン・表示名・URL・状態・更新日・操作）。展開中は直下に編集フォームを表示する。 */
function AppListingRow({
  listing,
  isEditing,
  onEditToggle,
  onSaved,
  onWithdrawn,
}: {
  listing: AppListingInfo;
  isEditing: boolean;
  onEditToggle: () => void;
  onSaved: (l: AppListingInfo) => void;
  onWithdrawn: () => void;
}) {
  const [withdrawing, setWithdrawing] = useState(false);

  const withdraw = async () => {
    if (!window.confirm(`「${listing.name}」の掲載申請を取り下げますか？`)) return;
    setWithdrawing(true);
    try {
      await deleteMyAppListing(listing.appId);
      onWithdrawn();
    } catch (e) {
      window.alert(String(e));
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <div className="app-listings__item">
      <div className="app-listings__row">
        <span className={`app-icon app-icon--md ${!listing.iconUrl ? `app-icon--tone${toneForApp(listing)}` : ''}`}>
          {listing.iconUrl ? <img src={listing.iconUrl} alt="" /> : listing.name.charAt(0).toUpperCase()}
        </span>
        <div className="app-listings__info">
          <div className="app-listings__name">
            {listing.name}
            <AppListingStatusBadge status={listing.status} />
          </div>
          <div className="app-listings__url">{listing.url}</div>
        </div>
        <div className="app-listings__updated">{new Date(listing.updatedAt).toLocaleDateString('ja-JP')}</div>
        <div className="app-listings__actions">
          {listing.status !== 'suspended' && (
            <button
              type="button"
              className={listing.status === 'rejected' ? 'btn btn--primary' : 'btn'}
              disabled={withdrawing}
              onClick={onEditToggle}
            >
              {isEditing ? '閉じる' : listing.status === 'rejected' ? '再申請する' : '編集'}
            </button>
          )}
          <button type="button" className="btn" disabled={withdrawing} onClick={withdraw}>
            {withdrawing ? '処理中…' : '取り下げる'}
          </button>
        </div>
        {listing.status === 'suspended' && <p className="app-listings__note">管理者により停止中です</p>}
      </div>
      {isEditing && (
        <AppListingEditForm
          listing={listing}
          onSaved={(l) => {
            onSaved(l);
            onEditToggle();
          }}
          onCancel={onEditToggle}
        />
      )}
    </div>
  );
}

/** 開発者ポータル「連携アプリケーション」の掲載申請一覧セクション（複数アプリ対応）。 */
function AppListingsSection() {
  const [listings, setListings] = useState<AppListingInfo[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = () => {
    fetchMyAppListings()
      .then(setListings)
      .catch((e) => setLoadError(String(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const atLimit = (listings?.length ?? 0) >= MAX_APP_LISTINGS;

  return (
    <section id="app-listings" style={{ marginTop: 24 }}>
      <div className="app-listings__head">
        <h3>連携アプリケーション</h3>
        <button type="button" className="btn btn--primary" onClick={() => setModalOpen(true)} disabled={atLimit}>
          + 新しいアプリを申請する
        </button>
      </div>
      <p>
        ホームの「連携アプリケーション」への掲載を申請したアプリの一覧です。アプリごとに編集（保存すると再審査）・取り下げができます。
      </p>

      {atLimit && (
        <div className="notice" style={{ marginBottom: 16 }}>
          掲載申請できるアプリ数の上限（{MAX_APP_LISTINGS}件）に達しています。
        </div>
      )}

      {loadError && <p className="viewer__error">{loadError}</p>}

      {listings === null ? (
        <Spinner />
      ) : listings.length === 0 ? (
        <p className="app-listings__empty">まだ申請したアプリはありません。</p>
      ) : (
        <div className="app-listings__list">
          {listings.map((l) => (
            <AppListingRow
              key={l.appId}
              listing={l}
              isEditing={editingId === l.appId}
              onEditToggle={() => setEditingId((cur) => (cur === l.appId ? null : l.appId))}
              onSaved={(updated) =>
                setListings((prev) => (prev ?? []).map((p) => (p.appId === updated.appId ? updated : p)))
              }
              onWithdrawn={() => {
                setListings((prev) => (prev ?? []).filter((p) => p.appId !== l.appId));
                setEditingId((cur) => (cur === l.appId ? null : cur));
              }}
            />
          ))}
        </div>
      )}

      <NewAppListingModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onCreated={(created) => {
          setListings((prev) => [...(prev ?? []), created]);
          setModalOpen(false);
        }}
      />
    </section>
  );
}

function PlanSection({ dev }: { dev: MyDeveloper }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const monthlyUsage = dev.usage
    .filter((u) => u.date.startsWith(currentMonth))
    .reduce((sum, u) => sum + u.requests, 0);
  // 管理者アカウント（unlimited）はクォータが存在しないため、超過表示の対象外にする。
  const overQuota = !dev.unlimited && monthlyUsage > dev.monthlyQuota;

  return (
    <section id="plan-usage" style={{ marginTop: 24 }}>
      <h3>API 利用状況</h3>

      <div className="plan-usage">
        <div className="plan-usage__row">
          <span>今月の使用量</span>
          <span className={`plan-usage__count${overQuota ? ' plan-usage__count--over' : ''}`}>
            {dev.unlimited ? `${monthlyUsage} / 無制限` : `${monthlyUsage} / ${dev.monthlyQuota}`}
          </span>
        </div>
        {!dev.unlimited && (
          <progress
            className={`plan-usage__bar${overQuota ? ' plan-usage__bar--over' : ''}`}
            value={Math.min(monthlyUsage, dev.monthlyQuota)}
            max={Math.max(dev.monthlyQuota, 1)}
          />
        )}
      </div>

      {!dev.unlimited && (
        <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
          月間 {dev.monthlyQuota.toLocaleString('ja-JP')} リクエストまで無料でご利用いただけます。それ以上の利用が必要な場合は{' '}
          <Link to={OPERATOR.contactPath}>お問い合わせ</Link>ください。
        </p>
      )}
    </section>
  );
}

function UsageTable({ usage }: { usage: DailyUsage[] }) {
  if (usage.length === 0) {
    return <p>まだ利用がありません。</p>;
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>日付</th>
          <th>リクエスト</th>
          <th>エラー</th>
          <th>429</th>
        </tr>
      </thead>
      <tbody>
        {usage.map((u) => (
          <tr key={u.date}>
            <td>{u.date}</td>
            <td>{u.requests}</td>
            <td>{u.errors}</td>
            <td>{u.rateLimited}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---- 開発者ポータル（承認済み開発者向け）の 2 カラムレイアウト: サイドナビ ----------------

type NavGroup = { title: string; items: { path: string; label: string }[] };

const DEV_PORTAL_NAV_GROUPS: NavGroup[] = [
  {
    title: '接続設定',
    items: [
      { path: '/developers/portal/oauth-client', label: 'OAuth クライアント' },
    ],
  },
  {
    title: '公開・配信',
    items: [
      { path: '/developers/portal/app-distribution', label: 'アニメーション配信設定' },
      { path: '/developers/portal/app-listings', label: '連携アプリケーション' },
    ],
  },
  {
    title: '利用状況',
    items: [
      { path: '/developers/portal/plan', label: 'プランと使用量' },
      { path: '/developers/portal/usage-log', label: '直近の利用ログ' },
    ],
  },
];

function DevPortalNav() {
  return (
    <nav className="dev-portal-nav" aria-label="開発者ポータル内ナビゲーション">
      {DEV_PORTAL_NAV_GROUPS.map((group) => (
        <div className="dev-portal-nav__group" key={group.title}>
          <div className="dev-portal-nav__heading">{group.title}</div>
          {group.items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `dev-portal-nav__item${isActive ? ' dev-portal-nav__item--active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function DevPortalLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="dev-portal-layout">
      <DevPortalNav />
      <div className="dev-portal-content">{children}</div>
    </div>
  );
}

export default function DevPortal() {
  const { user, ready, login } = useAuth();
  const [dev, setDev] = useState<MyDeveloper | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetchMyDeveloper()
      .then(setDev)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [user]);

  if (!ready) {
    return (
      <div className="container">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container">
        <div className="section-title">
          <h2>開発者登録</h2>
        </div>
        <div className="notice" style={{ marginBottom: 20 }}>
          開発者登録の申請にはログインが必要です。
        </div>
        <button className="btn btn--primary" onClick={login}>
          ログイン / 新規登録
        </button>
      </div>
    );
  }

  const refresh = (updated: DeveloperInfo) => {
    setDev({ ...updated, usage: dev?.usage ?? [] });
  };

  const showingApplyForm = !dev || dev.status === 'rejected';
  const pageTitle = showingApplyForm ? '開発者登録' : '開発者ポータル';

  return (
    <div className="container">
      <div className="section-title">
        <h2>{pageTitle}</h2>
        {dev && <StatusBadge status={dev.status} />}
      </div>

      {loading && <Spinner />}
      {error && <p className="viewer__error">{error}</p>}

      {!loading && !error && (
        dev?.status === 'approved' ? (
          <Routes>
            <Route
              path="oauth-client"
              element={
                <DevPortalLayout>
                  <OAuthClientSection dev={dev} onUpdated={(d) => refresh(d)} />
                </DevPortalLayout>
              }
            />
            <Route path="client-secret" element={<Navigate to="/developers/portal/oauth-client" replace />} />
            <Route path="app-distribution" element={<DevPortalLayout><AppDistributionSection dev={dev} /></DevPortalLayout>} />
            <Route path="app-listings" element={<DevPortalLayout><AppListingsSection /></DevPortalLayout>} />
            <Route path="plan" element={<DevPortalLayout><PlanSection dev={dev} /></DevPortalLayout>} />
            <Route
              path="usage-log"
              element={
                <DevPortalLayout>
                  <section style={{ marginTop: 24 }}>
                    <h3>直近の利用ログ</h3>
                    <p style={{ opacity: 0.8, fontSize: 13 }}>
                      直近の API リクエスト実績です。今月の使用量やクォータは{' '}
                      <Link to="/developers/portal/plan">プランと使用量</Link>{' '}
                      でご確認いただけます。
                    </p>
                    <UsageTable usage={dev.usage} />
                  </section>
                </DevPortalLayout>
              }
            />
            <Route index element={<Navigate to="/developers/portal/oauth-client" replace />} />
            <Route path="*" element={<Navigate to="/developers/portal/oauth-client" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route
              index
              element={
                <>
                  {!dev && <ApplyForm rejected={false} onSubmitted={(d) => refresh(d)} />}

                  {dev?.status === 'pending' && (
                    <div className="notice">
                      申請を審査中です。承認されるまでしばらくお待ちください。
                    </div>
                  )}

                  {dev?.status === 'rejected' && (
                    <ApplyForm rejected onSubmitted={(d) => refresh(d)} />
                  )}

                  {dev?.status === 'suspended' && (
                    <div className="notice">
                      開発者アカウントが停止されています。心当たりがない場合は
                      <Link to={OPERATOR.contactPath}>{OPERATOR.contactLabel}</Link>
                      へお問い合わせください。
                    </div>
                  )}

                  {dev && <PlanSection dev={dev} />}
                </>
              }
            />
            <Route path="*" element={<Navigate to="/developers/portal" replace />} />
          </Routes>
        )
      )}
    </div>
  );
}
